import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BillingExplorerSideBarPanelContentType,
  ExplainWithAiData,
  ResourceExplorerInterface,
  ResourceExplorerPath,
} from "./utils/types/billingExplorer";
import { ResourceExplorerDataGridView } from "./utils/types/resourceExplorerDataGrid";
import {
  ChartSettings,
  CostType,
  DateDataPoint,
  Dates,
  ForecastOption,
  ResourceExplorerData,
  ResourceExplorerGroupingNullable,
  TrendType,
  XAxisGranularity,
} from "@/services/cloudchipr.api";
import { FilterTreeNodeWithId } from "@/components/pages/common/filters-tree/utils/types/common";

const initialState: ResourceExplorerInterface = {
  id: "",
  data: null,
  filterTree: undefined,
  path: [],
  sideBarPanelContentType: null,
  dataGridView: null,
  csvDataHash: "",
  explainWithAi: { data: [] },
  chartSettings: null,
  forecastSuggestionPopoverOpen: false,
};

export const resourceExplorerSlice = createSlice({
  name: "resourceExplorer",
  initialState: initialState,
  reducers: {
    setResourceExplorerId: (state, action: PayloadAction<string>) => {
      state.id = action.payload;
    },

    setResourceExplorerExplainWithAIData: (
      state,
      action: PayloadAction<ExplainWithAiData[]>,
    ) => {
      state.explainWithAi.data = action.payload;
    },
    setResourceExplorerDataGridView: (
      state,
      action: PayloadAction<ResourceExplorerDataGridView>,
    ) => {
      state.dataGridView = action.payload;
    },
    setResourceExplorerData: (
      state,
      action: PayloadAction<ResourceExplorerData>,
    ) => {
      const { date_label, from_date, to_date } = action.payload;

      state.data = {
        ...action.payload,
        from_date: date_label && from_date ? null : from_date,
        to_date: date_label && to_date ? null : to_date,
      };
    },
    setResourceExplorerGrouping: (
      state,
      action: PayloadAction<{
        grouping: ResourceExplorerGroupingNullable;
        groupValues?: string[];
      }>,
    ) => {
      if (!state.data) {
        return;
      }

      state.data.grouping = action.payload.grouping;
      state.data.group_values = action.payload.groupValues;
    },
    setResourceExplorerAxisGranularity: (
      state,
      action: PayloadAction<XAxisGranularity>,
    ) => {
      if (!state.data) {
        return;
      }
      state.data.axis_granularity = action.payload;
    },
    setResourceExplorerDataPoint: (
      state,
      action: PayloadAction<DateDataPoint>,
    ) => {
      if (!state.data) {
        return;
      }

      state.data.data_point = action.payload;
    },
    setResourceExplorerDates: (state, action: PayloadAction<Dates>) => {
      if (!state.data) {
        return;
      }

      state.data.dates = action.payload;
    },
    setResourceExplorerForecastOption: (
      state,
      action: PayloadAction<ForecastOption>,
    ) => {
      if (!state.data) {
        return;
      }

      state.data.forecast_option = action.payload;
    },
    resetResourceExplorer: () => {
      return initialState;
    },

    setResourceExplorerPath: (
      state,
      action: PayloadAction<ResourceExplorerPath[]>,
    ) => {
      state.path = action.payload;
    },
    toggleShowBudgetLine: (state) => {
      if (!state.chartSettings) {
        return;
      }

      state.chartSettings.show_budget_line =
        !state.chartSettings.show_budget_line;
    },
    setResourceExplorerCsvDataHash: (state, action: PayloadAction<string>) => {
      state.csvDataHash = action.payload;
    },
    setResourceExplorerTrendType: (state, action: PayloadAction<TrendType>) => {
      if (!state.data) {
        return;
      }

      state.data.trend_type = action.payload;
    },
    setResourceExplorerCostType: (state, action: PayloadAction<CostType>) => {
      if (!state.data) {
        return;
      }

      state.data.cost_type = action.payload;
    },
    setResourceExplorerFilterTree: (
      state,
      action: PayloadAction<FilterTreeNodeWithId | undefined>,
    ) => {
      state.filterTree = action.payload;
    },

    setBillingExplorerSideBarPanelContentType: (
      state,
      action: PayloadAction<BillingExplorerSideBarPanelContentType | null>,
    ) => {
      state.sideBarPanelContentType = action.payload;
    },

    setForecastSuggestionPopoverOpen: (
      state,
      action: PayloadAction<
        ResourceExplorerInterface["forecastSuggestionPopoverOpen"]
      >,
    ) => {
      state.forecastSuggestionPopoverOpen = action.payload;
    },

    // chart settings
    setBillingExplorerChartSettings: (
      state,
      action: PayloadAction<Partial<ChartSettings>>,
    ) => {
      state.chartSettings = { ...state.chartSettings, ...action.payload };
    },
  },
});

export const {
  setResourceExplorerPath,
  resetResourceExplorer,
  setResourceExplorerData,
  setResourceExplorerDataPoint,
  setResourceExplorerGrouping,
  setResourceExplorerAxisGranularity,
  setResourceExplorerDates,
  setResourceExplorerId,
  setResourceExplorerForecastOption,
  setResourceExplorerDataGridView,
  toggleShowBudgetLine,
  setResourceExplorerCsvDataHash,
  setResourceExplorerTrendType,
  setResourceExplorerCostType,
  setResourceExplorerFilterTree,
  setResourceExplorerExplainWithAIData,
  setForecastSuggestionPopoverOpen,
  setBillingExplorerChartSettings,
  setBillingExplorerSideBarPanelContentType,
} = resourceExplorerSlice.actions;

export default resourceExplorerSlice.reducer;
